/**
 * Relative timestamps for list views: "3 days ago", with the exact date on hover.
 *
 * Sorting is always done on the timestamp, never on the string these produce — a list
 * ordered by "N months ago" would be ordered alphabetically, which is nonsense.
 *
 * `now` is injectable so callers can render a stable value in tests instead of racing
 * the clock, the same shape the login accounting on the Worker uses.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Whole days between two instants, floored, so anything inside 24 hours reads as "Today". */
export function daysAgo(timestamp: number, now: number = Date.now()): number {
  return Math.max(0, Math.floor((now - timestamp) / MS_PER_DAY));
}

export function formatRelativeDays(days: number): string {
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 31) return `${days} days ago`;
  if (days < 365) return `${Math.round(days / 30)} months ago`;
  return `${(days / 365).toFixed(1)} years ago`;
}

export function formatRelativeTime(timestamp: number, now: number = Date.now()): string {
  return formatRelativeDays(daysAgo(timestamp, now));
}

/** A calendar date on its own, for columns that read as a date rather than an age. */
export function formatAbsoluteDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

/** The precise value the relative label hangs off, for a tooltip or a title attribute. */
export function formatAbsoluteTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
